<template>
	<div class="catchment-comparison-card" :class="{ 'catchment-comparison-card--gap': row.ecart }">
		<ProductLabel class="catchment-comparison-card__header" :params="{ value: row.label, subtitle: row.ean, flag: row.ecart, flagTooltip: gapTooltip, imageThumbnailUid: row.imageThumbnailUid, productId: row.productId, avatarSize: 40 }" />

		<div class="catchment-comparison-card__stores">
			<div v-for="store in stores" :key="store.deliveryLocationId ?? 'reference'" class="catchment-comparison-card__store">
				<div class="catchment-comparison-card__store-identity">
					<v-avatar size="24" :color="logoUrlFor(store) ? 'white' : store.badgeColor" class="catchment-comparison-card__store-avatar">
						<v-img v-if="logoUrlFor(store)" :src="logoUrlFor(store)" alt="" />
						<span v-else class="catchment-comparison-card__store-avatar-letter">{{ store.badgeLetter }}</span>
					</v-avatar>
					<span class="catchment-comparison-card__store-name">{{ store.label }}</span>
					<v-chip v-if="store.isReference" size="x-small" color="error" variant="flat" class="catchment-comparison-card__store-tag" readonly>{{ t('Visited') }}</v-chip>
					<span v-else-if="store.posType" class="catchment-comparison-card__store-type">{{ store.posType }}</span>
				</div>

				<div class="catchment-comparison-card__metrics metric-tile-grid" style="--metric-tile-grid-columns: 5">
					<MetricTile :label="t('Status')" :value="stateLabel(store.state)" :color="stateColor(store.state)" />
					<MetricTile :label="t('Facing')" :value="store.facing ?? '—'" />
					<MetricTile :label="t('Price')" :value="formatValue(store.retailPrice, { decimals: 2 })" />
					<MetricTile label="VMH">
						<span class="catchment-comparison-card__metric-value" :class="trendClass(store.vmhTrend)">
							<v-icon v-if="store.vmhTrend === 'up'" size="10">trending_up</v-icon>
							<v-icon v-else-if="store.vmhTrend === 'down'" size="10">trending_down</v-icon>
							{{ formatNumber(store.vmh) }}
						</span>
					</MetricTile>
					<MetricTile label="VMM">
						<span class="catchment-comparison-card__metric-value" :class="trendClass(store.vmmTrend)">
							<v-icon v-if="store.vmmTrend === 'up'" size="10">trending_up</v-icon>
							<v-icon v-else-if="store.vmmTrend === 'down'" size="10">trending_down</v-icon>
							{{ formatNumber(store.vmm) }}
						</span>
					</MetricTile>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watchEffect } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { formatValue, formatNumber } from '@/utils/core/format';
import { STATEMENT_STATE } from '@/utils/domain/catchmentAreaComparison';
import { getAccountImageUrl } from '@/api/core/file.js';
import ProductLabel from '@/components/common/ProductLabel.vue';
import MetricTile from '@/components/common/MetricTile.vue';

const props = defineProps({
	row: { type: Object, required: true },
	stores: { type: Array, default: () => [] },
	gapTooltip: { type: String, default: '' },
});

const t = useTranslator();

const logoUrlByStoreKey = ref({});
function storeKey(store) {
	return store?.deliveryLocationId ?? 'reference';
}
function logoUrlFor(store) {
	return logoUrlByStoreKey.value[storeKey(store)] || null;
}
watchEffect(async () => {
	const entries = await Promise.all((props.stores || []).map(async (store) => [storeKey(store), store.accountImageUid ? await getAccountImageUrl(store.accountImageUid) : null]));
	logoUrlByStoreKey.value = Object.fromEntries(entries);
});

function trendClass(trend) {
	if (trend === 'up') return 'catchment-comparison-card__metric-trend--up';
	if (trend === 'down') return 'catchment-comparison-card__metric-trend--down';
	return null;
}

function stateLabel(state) {
	if (state === STATEMENT_STATE.PRESENT) return t('Present');
	if (state === STATEMENT_STATE.ABSENT) return t('Absent');
	if (state === STATEMENT_STATE.OUT_OF_STOCK) return t('Out Of Stock');
	return '—';
}

function stateColor(state) {
	if (state === STATEMENT_STATE.PRESENT) return 'success';
	if (state === STATEMENT_STATE.ABSENT) return 'info';
	if (state === STATEMENT_STATE.OUT_OF_STOCK) return 'error';
	return null;
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.catchment-comparison-card {
	border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 12px;
	background: rgb(var(--v-theme-surface));
	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	&--gap {
		background: rgba(var(--v-theme-error), 0.08);
	}

	&__header {
		height: auto !important;
		margin-bottom: 12px;
	}

	&__stores {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	&__store {
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		padding-top: 10px;

		&:first-child {
			border-top: none;
			padding-top: 0;
		}
	}

	&__store-identity {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	&__store-avatar {
		flex: none;
	}

	&__store-avatar-letter {
		font-size: $font-size-tiny;
		font-weight: 600;
		color: white;
	}

	&__store-tag {
		flex: none;
		font-size: $font-size-tiny;
	}

	&__store-type {
		flex: none;
		font-size: $font-size-tiny;
		font-weight: 600;
		text-transform: uppercase;
		color: rgba(var(--v-theme-on-surface), 0.6);
	}

	&__store-name {
		flex: 1 1 auto;
		min-width: 0;
		font-size: $font-size;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__metric-value {
		display: flex;
		align-items: center;
		gap: 2px;
	}

	&__metric-trend {
		&--up {
			color: #2e7d32;
		}

		&--down {
			color: #c62828;
		}
	}
}
</style>
